import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  UserPlus,
  X,
} from 'lucide-react';
import { api, type X5UserCreated } from '../lib/api';
import { Button } from './ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Badge } from './ui/Badge';
import { useOrgLookup } from '../lib/orgLookup';

interface Props {
  onClose: () => void;
}

export function CreateUserModal({ onClose }: Props) {
  const queryClient = useQueryClient();
  const lookup = useOrgLookup();
  const orgOptions = useMemo(
    () =>
      Array.from(lookup.byId.values()).sort((a, b) =>
        (a.name ?? a.company ?? a.id).localeCompare(b.name ?? b.company ?? b.id),
      ),
    [lookup.byId],
  );

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [usernameDirty, setUsernameDirty] = useState(false);
  const [organizationId, setOrganizationId] = useState('');

  const onEmailChange = (value: string) => {
    setEmail(value);
    if (!usernameDirty) setUsername(value.trim().toLowerCase());
  };

  const onUsernameChange = (value: string) => {
    setUsername(value);
    setUsernameDirty(true);
  };

  const mutation = useMutation({
    mutationFn: () =>
      api.createUser({
        email: email.trim().toLowerCase(),
        username: username.trim(),
        organizationId: organizationId === '' ? null : organizationId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      void queryClient.invalidateQueries({ queryKey: ['users', 'identities'] });
    },
  });

  const created = mutation.data;
  const valid = email.includes('@') && username.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-xl bg-card border-l border-border shadow-xl flex flex-col">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-muted-foreground" />
              {created ? 'User created' : 'Create user'}
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              {created
                ? 'Save the temporary password before closing — it is shown only once.'
                : 'Adds a new account to the xiltrix_customer Cognito pool.'}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {!created && (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (valid) mutation.mutate();
              }}
            >
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Email</label>
                <Input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="user@example.com"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Will be set as <code className="font-mono">email</code> +{' '}
                  <code className="font-mono">email_verified=true</code>.
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-muted-foreground">Username</label>
                  {usernameDirty && email.trim() && (
                    <button
                      type="button"
                      onClick={() => {
                        setUsername(email.trim().toLowerCase());
                        setUsernameDirty(false);
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Reset to email
                    </button>
                  )}
                </div>
                <Input
                  required
                  value={username}
                  onChange={(e) => onUsernameChange(e.target.value)}
                  placeholder="Defaults to email"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  No whitespace. This is what the user types at sign-in (not the email).
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">
                  Organization (optional)
                </label>
                <Select
                  value={organizationId}
                  onChange={(e) => setOrganizationId(e.target.value)}
                >
                  <option value="">— None (assign later) —</option>
                  {orgOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name ?? o.company ?? o.id}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Sets <code className="font-mono">organizationId</code> on the X5 User row.
                  Leave blank to create the user unassigned.
                </p>
              </div>

              {mutation.isError && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm flex gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Create failed</p>
                    <p className="text-xs mt-1">{(mutation.error as Error).message}</p>
                  </div>
                </div>
              )}

              <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
                <p className="font-medium text-foreground mb-1">What happens on submit</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Account created in the xiltrix_customer Cognito pool.</li>
                  <li>Temporary password generated (16 chars, mixed classes).</li>
                  <li>
                    Matching <code className="font-mono">User</code> row written to the X5 DDB
                    table (new UUID id, Amplify <code className="font-mono">_version=1</code>).
                  </li>
                  <li>Cognito's welcome email is suppressed — admin shares the password out of band.</li>
                  <li>User is forced to change the password on first sign-in.</li>
                </ul>
              </div>
            </form>
          )}

          {created && <CreatedView created={created} />}
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-end gap-2">
          {!created && (
            <>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={() => mutation.mutate()} disabled={!valid || mutation.isPending}>
                {mutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" />
                    Create user
                  </>
                )}
              </Button>
            </>
          )}
          {created && (
            <Button onClick={onClose}>
              <CheckCircle2 className="h-4 w-4" />
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CreatedView({ created }: { created: X5UserCreated }) {
  const [revealed, setRevealed] = useState(false);
  const lookup = useOrgLookup();
  const orgName = created.organizationId
    ? lookup.resolveName(created.organizationId)
    : null;

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-warning/40 bg-warning/10 text-warning px-4 py-3 text-sm flex gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <p className="font-medium">Save the temporary password now.</p>
          <p className="text-xs mt-1 opacity-90">
            It will not be shown again. The user must change it on first sign-in.
          </p>
        </div>
      </div>

      {created.databaseError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm flex gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Cognito account created but X5 row failed</p>
            <p className="text-xs mt-1 opacity-90">
              The Cognito user exists, but writing the X5{' '}
              <code className="font-mono">User</code> row failed. The new user will not appear on
              the X5 Users page until the row is created — check the API logs for the cause.
            </p>
          </div>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Credentials</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <CredentialRow label="Username" value={created.username} />
          <CredentialRow label="Email" value={created.email} />
          <CredentialRow label="X5 user ID" value={created.userId} />
          {orgName && (
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Organization
              </div>
              <div className="font-mono text-sm bg-muted/40 border border-border rounded-md px-3 py-2 min-h-9 flex items-center gap-2">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                {orgName}
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">
                Temporary password
              </span>
              <Badge variant="warning" className="text-[10px]">
                show once
              </Badge>
            </div>
            <div className="flex items-stretch gap-2">
              <div className="flex-1 font-mono text-sm bg-muted/40 border border-border rounded-md px-3 py-2 break-all min-h-9 flex items-center">
                {revealed ? created.temporaryPassword : '•'.repeat(created.temporaryPassword.length)}
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setRevealed((v) => !v)}
                title={revealed ? 'Hide' : 'Show'}
              >
                {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <CopyButton value={created.temporaryPassword} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CredentialRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</div>
      <div className="flex items-stretch gap-2">
        <div className="flex-1 font-mono text-sm bg-muted/40 border border-border rounded-md px-3 py-2 break-all min-h-9 flex items-center">
          {value}
        </div>
        <CopyButton value={value} />
      </div>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="outline"
      size="icon"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        } catch {
          // fall back silently — copy may be denied in some contexts
        }
      }}
      title={copied ? 'Copied' : 'Copy'}
    >
      {copied ? <CheckCircle2 className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}
